import React, { useState, useEffect, useRef } from 'react'
import {
  Drawer,
  ScrollArea,
  TextInput,
  ActionIcon,
  Group,
  Text,
  Avatar,
  Stack,
  useMantineTheme,
  Loader,
  Tooltip
} from '@mantine/core'
import { openConfirmModal } from '@mantine/modals'
import { IconSend, IconRotate, IconMicrophone, IconX, IconPlayerStop, IconSettings, IconCopy, IconCheck } from '@tabler/icons'
import { IconSparkles } from '@tabler/icons-react'
import { useStore } from '../../hooks/useStore'
import { useSessionStorage } from '@mantine/hooks'
import { useSpeechToText } from '../../hooks/useSpeechToText'
import { useOllama, OLLAMA_MISSING_CREDENTIALS_ERROR } from '../../hooks/useOllama'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { skills, generateSystemPrompt } from '../../skills'

interface Message {
  id: string
  sender: 'user' | 'ai' | 'tool'
  text: string
  timestamp: Date | string
  toolName?: string
  toolStatus?: 'running' | 'success' | 'error'
  toolArgs?: any
}

export default function AiSidebar() {
  const theme = useMantineTheme()
  const aiSidebarOpened = useStore((state: any) => state.aiSidebarOpened)
  const setAiSidebarOpened = useStore((state: any) => state.setAiSidebarOpened)
  const pendingAiPrompt = useStore((state: any) => state.pendingAiPrompt)
  const setPendingAiPrompt = useStore((state: any) => state.setPendingAiPrompt)

  const [inputValue, setInputValue] = useState('')
  const [isTyping, setIsTyping] = useState(false)
  const [hoveredMessageId, setHoveredMessageId] = useState<string | null>(null)
  const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null)
  const scrollViewportRef = useRef<HTMLDivElement>(null)
  const processedPromptRef = useRef<string>('')
  const recursionCountRef = useRef<number>(0)

  const { isListening, toggleListening } = useSpeechToText((transcript) => {
    setInputValue((prev) => (prev ? `${prev} ${transcript}` : transcript))
  })

  const { streamChat, isLoading, stopStreaming } = useOllama()

  const getLocalStorageValue = (key: string, defaultValue: string = ''): string => {
    const val = localStorage.getItem(key)
    if (!val) return defaultValue
    try {
      return JSON.parse(val)
    } catch {
      return val
    }
  }

  const generateUniqueId = (): string => {
    return `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`
  }

  const [messages, setMessages] = useSessionStorage<Message[]>({
    key: 'aiSideBarConversationHistory',
    defaultValue: [
      {
        id: '1',
        sender: 'ai',
        text: 'Szia! Én vagyok a Builty AI asszisztensed. Miben segíthetek ma?',
        timestamp: new Date(Date.now() - 60000 * 5)
      }
    ],
    deserialize: (str) => {
      if (!str) return [];
      try {
        const parsed = JSON.parse(str) as any[];
        return parsed.map((msg) => ({
          ...msg,
          timestamp: new Date(msg.timestamp)
        }));
      } catch {
        return [];
      }
    }
  })

  const scrollToBottom = (behavior: 'smooth' | 'auto' = 'smooth') => {
    if (scrollViewportRef.current) {
      scrollViewportRef.current.scrollTo({
        top: scrollViewportRef.current.scrollHeight,
        behavior
      })
    }
  }

  const handleCopyText = (msgId: string, text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedMessageId(msgId)
      setTimeout(() => {
        setCopiedMessageId(null)
      }, 2000)
    }).catch((err) => {
      console.error('Nem sikerült a szöveg másolása:', err)
    })
  }

  useEffect(() => {
    if (aiSidebarOpened) {
      scrollToBottom('auto')
      const t1 = setTimeout(() => scrollToBottom('auto'), 100)
      const t2 = setTimeout(() => scrollToBottom('auto'), 250)
      const t3 = setTimeout(() => scrollToBottom('smooth'), 500)
      return () => {
        clearTimeout(t1)
        clearTimeout(t2)
        clearTimeout(t3)
      }
    }
  }, [aiSidebarOpened])

  useEffect(() => {
    scrollToBottom('smooth')
  }, [messages, isTyping])

  const startStreamingResponse = (updatedMessages: Message[]) => {
    setIsTyping(true)
    const aiMessageId = generateUniqueId()

    const systemPrompt = generateSystemPrompt({ isMobile: false })
    const ollamaMessages = [
      { role: 'system', content: systemPrompt },
      ...updatedMessages.map((msg) => {
        if (msg.sender === 'tool') {
          return {
            role: 'user',
            content: `[ESZKÖZ VÁLASZA - ${msg.toolName}]: ${msg.text}`
          }
        }
        return {
          role: msg.sender === 'user' ? 'user' : 'assistant',
          content: msg.text
        }
      })
    ]

    streamChat(ollamaMessages, {
      onChunk: (chunk) => {
        setIsTyping(false)
        setMessages((mList) => {
          const exists = mList.some((msg) => msg.id === aiMessageId)
          if (!exists) {
            return [
              ...mList,
              {
                id: aiMessageId,
                sender: 'ai',
                text: chunk,
                timestamp: new Date()
              }
            ]
          }
          return mList.map((msg) =>
            msg.id === aiMessageId ? { ...msg, text: msg.text + chunk } : msg
          )
        })
      },
      onDone: async (fullText) => {
        setIsTyping(false)

        // Construct message history including the final assistant response
        const finalAiMsg: Message = {
          id: aiMessageId,
          sender: 'ai' as const,
          text: fullText,
          timestamp: new Date()
        }
        const listWithFinalAi = [...updatedMessages, finalAiMsg]
        setMessages(listWithFinalAi)

        // Check if response contains tool execution call
        const match = fullText.match(/EXECUTE:(\w+)\(([\s\S]*?)\)/)
        if (match) {
          const toolName = match[1]
          const argsStr = match[2].trim()
          let args = {}
          if (argsStr) {
            try {
              args = JSON.parse(argsStr)
            } catch (e) {
              console.error("Nem sikerült JSON argumentumot parse-olni:", argsStr, e)
            }
          }

          if (recursionCountRef.current >= 5) {
            console.warn("Max recursion limit of 5 turns reached.")
            return
          }

          const skill = skills[toolName]
          if (skill) {
            recursionCountRef.current += 1
            const toolMsgId = generateUniqueId()

            // Construct running tool message and update state
            const runningToolMsg: Message = {
              id: toolMsgId,
              sender: 'tool',
              text: 'Futás...',
              toolName: toolName,
              toolStatus: 'running',
              toolArgs: args,
              timestamp: new Date()
            }
            const listWithRunning = [...listWithFinalAi, runningToolMsg]
            setMessages(listWithRunning)

            // Execute the tool asynchronously outside state updater to prevent duplicate React triggers
            const runTool = async () => {
              try {
                const result = await skill.execute(args)
                const resultString = typeof result === 'string' ? result : JSON.stringify(result)

                const listWithSuccess = listWithRunning.map((msg) =>
                  msg.id === toolMsgId
                    ? { ...msg, text: resultString, toolStatus: 'success' as const }
                    : msg
                )
                setMessages(listWithSuccess)
                startStreamingResponse(listWithSuccess)
              } catch (err: any) {
                console.error(`Hiba a(z) ${toolName} futtatásakor:`, err)
                const errStr = err?.message || String(err)

                const listWithError = listWithRunning.map((msg) =>
                  msg.id === toolMsgId
                    ? { ...msg, text: `Hiba: ${errStr}`, toolStatus: 'error' as const }
                    : msg
                )
                setMessages(listWithError)
                startStreamingResponse(listWithError)
              }
            }

            runTool()
          } else {
            console.error(`A(z) "${toolName}" eszköz nem található a regisztrált skillek között.`)
          }
        }
      },
      onError: (error) => {
        setIsTyping(false)
        setMessages((mList) => [
          ...mList,
          {
            id: generateUniqueId(),
            sender: 'ai',
            text: `Hiba történt: ${error}`,
            timestamp: new Date()
          }
        ])
      }
    })
  }

  useEffect(() => {
    if (!pendingAiPrompt) {
      processedPromptRef.current = ''
      return
    }

    if (pendingAiPrompt.trim() && processedPromptRef.current !== pendingAiPrompt) {
      processedPromptRef.current = pendingAiPrompt
      const promptText = pendingAiPrompt.trim()

      const userMsg: Message = {
        id: generateUniqueId(),
        sender: 'user',
        text: promptText,
        timestamp: new Date()
      }

      setPendingAiPrompt('')
      recursionCountRef.current = 0

      const apiKey = getLocalStorageValue('ai_api_key')
      const modelTag = getLocalStorageValue('ai_model_tag')

      if (!apiKey.trim() || !modelTag.trim()) {
        setIsTyping(false)
        setMessages((prev) => [
          ...prev,
          userMsg,
          {
            id: generateUniqueId(),
            sender: 'ai',
            text: OLLAMA_MISSING_CREDENTIALS_ERROR,
            timestamp: new Date()
          }
        ])
        return
      }

      const updated = [...messages, userMsg]
      setMessages(updated)
      startStreamingResponse(updated)
    }
  }, [pendingAiPrompt, setPendingAiPrompt])

  const handleSendMessage = () => {
    if (!inputValue.trim() || isLoading) return

    const userMsg: Message = {
      id: generateUniqueId(),
      sender: 'user',
      text: inputValue.trim(),
      timestamp: new Date()
    }

    setInputValue('')
    recursionCountRef.current = 0

    const apiKey = getLocalStorageValue('ai_api_key')
    const modelTag = getLocalStorageValue('ai_model_tag')

    if (!apiKey.trim() || !modelTag.trim()) {
      setIsTyping(false)
      setMessages((prev) => [
        ...prev,
        userMsg,
        {
          id: generateUniqueId(),
          sender: 'ai',
          text: OLLAMA_MISSING_CREDENTIALS_ERROR,
          timestamp: new Date()
        }
      ])
      return
    }

    const updated = [...messages, userMsg]
    setMessages(updated)
    startStreamingResponse(updated)
  }

  const handleClearHistory = () => {
    openConfirmModal({
      title: 'Beszélgetés törlése',
      centered: true,
      children: (
        <Text size="sm">
          Biztosan törölni szeretnéd a beszélgetési előzményeket? Ezt a műveletet nem lehet visszavonni.
        </Text>
      ),
      labels: { confirm: 'Törlés', cancel: 'Mégsem' },
      confirmProps: { color: 'red' },
      onConfirm: () => {
        setMessages([
          {
            id: generateUniqueId(),
            sender: 'ai',
            text: 'Szia! Én vagyok a Builty AI asszisztensed. Miben segíthetek ma?',
            timestamp: new Date()
          }
        ])
      }
    })
  }

  const hasConversation = messages && messages.length > 1

  return (
    <>
      <Drawer
        opened={aiSidebarOpened}
        onClose={() => setAiSidebarOpened(false)}
        position="right"
        size={"50%"}
        padding={0}
        withCloseButton={false}
        styles={(theme) => ({
          drawer: {
            backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[8] : theme.white,
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            borderLeft: `1px solid ${theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.gray[2]}`
          },
          body: {
            height: '100%'
          }
        })}
      >
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* Drawer Header */}
          <Group
            position="apart"
            px="md"
            py="sm"
            style={{
              borderBottom: `1px solid ${theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.gray[2]}`,
              height: '60px'
            }}
          >
            <Group spacing="xs">
              <IconSparkles size={22} color={theme.colors.green[6]} />
              <div>
                <Text weight={700} size="md" style={{ color: theme.colorScheme === 'dark' ? theme.white : theme.black }}>
                  AI Asszisztens
                </Text>
                <Text size="xs" color="dimmed">
                  Builty AI
                </Text>
              </div>
            </Group>

            <Group spacing="md" align="center">
              <ActionIcon onClick={handleClearHistory} title="Csevegés alaphelyzetbe állítása" variant="subtle" color="gray">
                <IconRotate size={18} />
              </ActionIcon>
              <ActionIcon
                onClick={() => setAiSidebarOpened(false)}
                variant="subtle"
                color="gray"
              >
                <IconX size={18} />
              </ActionIcon>
            </Group>
          </Group>

          {/* Scrollable Chat Message List */}
          <ScrollArea style={{ flex: 1, padding: '16px' }} viewportRef={scrollViewportRef}>
            <Stack spacing="md">
              {messages.map((msg) => {
                if (msg.text.includes('EXECUTE:')) {
                  return null
                }

                const isUser = msg.sender === 'user'
                const isTool = msg.sender === 'tool'

                if (isTool) {
                  const skill = msg.toolName ? skills[msg.toolName] : null
                  const uiText = skill ? skill.outputTextForUI : (msg.toolName || 'Eszköz')

                  let statusSuffix = 'futtatás...'
                  if (msg.toolStatus === 'success') {
                    statusSuffix = 'Sikeres'
                  } else if (msg.toolStatus === 'error') {
                    statusSuffix = 'Hiba'
                  }

                  const argsStr = msg.toolArgs ? JSON.stringify(msg.toolArgs) : ''
                  const hasArgs = argsStr && argsStr !== '{}' && Object.keys(msg.toolArgs).length > 0
                  const paramsText = hasArgs ? argsStr : 'nincs'

                  return (
                    <div
                      key={msg.id}
                      style={{
                        display: 'flex',
                        justifyContent: 'flex-start',
                        width: '100%'
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'flex-start',
                          gap: '8px',
                          maxWidth: '85%',
                          minWidth: 0
                        }}
                      >
                        <Avatar color="blue" radius="xl" size="sm" style={{ marginTop: '4px', flexShrink: 0 }}>
                          <IconSettings size={16} />
                        </Avatar>
                        <div
                          style={{
                            backgroundColor: theme.colorScheme === 'dark'
                              ? theme.colors.dark[6]
                              : theme.colors.gray[1],
                            color: theme.colorScheme === 'dark' ? theme.colors.dark[0] : theme.black,
                            padding: '10px 14px',
                            borderRadius: '16px 16px 16px 2px',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '4px',
                            minWidth: 0
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            {msg.toolStatus === 'running' && <Loader size="xs" color="blue" variant="dots" />}
                            <Text size="sm" weight={700} style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                              {uiText}: {statusSuffix}
                            </Text>
                          </div>
                          <Text size="xs" color="dimmed" style={{ fontStyle: 'italic', wordBreak: 'break-all', overflowWrap: 'break-word' }}>
                            Eszköznév: {msg.toolName}
                          </Text>
                          <Text size="xs" color="dimmed" style={{ fontStyle: 'italic', wordBreak: 'break-all', overflowWrap: 'break-word' }}>
                            Paraméterek: {paramsText}
                          </Text>
                        </div>
                      </div>
                    </div>
                  )
                }

                return (
                  <div
                    key={msg.id}
                    style={{
                      display: 'flex',
                      justifyContent: isUser ? 'flex-end' : 'flex-start',
                      width: '100%'
                    }}
                  >
                    <div
                      onMouseEnter={() => setHoveredMessageId(msg.id)}
                      onMouseLeave={() => setHoveredMessageId(null)}
                      style={{
                        display: 'flex',
                        flexDirection: isUser ? 'row-reverse' : 'row',
                        alignItems: 'flex-start',
                        gap: '8px',
                        maxWidth: '85%',
                        minWidth: 0,
                        position: 'relative',
                        paddingRight: isUser ? 0 : '32px',
                        paddingLeft: isUser ? '32px' : 0
                      }}
                    >
                      {!isUser && (
                        <Avatar color="green" radius="xl" size="sm" style={{ marginTop: '4px', flexShrink: 0 }}>
                          <IconSparkles size={16} />
                        </Avatar>
                      )}
                      <div
                        style={{
                          backgroundColor: isUser
                            ? theme.colors.green[8]
                            : theme.colorScheme === 'dark'
                              ? theme.colors.dark[6]
                              : theme.colors.gray[1],
                          color: isUser ? theme.white : theme.colorScheme === 'dark' ? theme.colors.dark[0] : theme.black,
                          padding: '10px 14px',
                          borderRadius: isUser ? '16px 16px 2px 16px' : '16px 16px 16px 2px',
                          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                          minWidth: 0
                        }}
                      >
                        {isUser ? (
                          <Text size="sm" style={{ wordBreak: 'break-word', overflowWrap: 'break-word', whiteSpace: 'pre-line' }}>
                            {msg.text}
                          </Text>
                        ) : (
                          <div style={{ wordBreak: 'break-word', overflowWrap: 'break-word', fontSize: theme.fontSizes.sm }}>
                            <ReactMarkdown
                              remarkPlugins={[remarkGfm]}
                              components={{
                                p: ({ children }) => <p style={{ margin: 0, whiteSpace: 'pre-line', overflowWrap: 'break-word', wordBreak: 'break-word' }}>{children}</p>,
                                ul: ({ children }) => <ul style={{ margin: '4px 0', paddingLeft: '20px' }}>{children}</ul>,
                                ol: ({ children }) => <ol style={{ margin: '4px 0', paddingLeft: '20px' }}>{children}</ol>,
                                li: ({ children }) => <li style={{ margin: '2px 0', overflowWrap: 'break-word', wordBreak: 'break-word' }}>{children}</li>,
                                h1: ({ children }) => <Text size="md" weight={700} style={{ margin: '8px 0 4px 0', overflowWrap: 'break-word', wordBreak: 'break-word' }}>{children}</Text>,
                                h2: ({ children }) => <Text size="sm" weight={700} style={{ margin: '8px 0 4px 0', overflowWrap: 'break-word', wordBreak: 'break-word' }}>{children}</Text>,
                                h3: ({ children }) => <Text size="sm" weight={700} style={{ margin: '8px 0 4px 0', overflowWrap: 'break-word', wordBreak: 'break-word' }}>{children}</Text>,
                                code: ({ children }) => (
                                  <code style={{
                                    wordBreak: 'break-all',
                                    whiteSpace: 'pre-wrap',
                                    backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[8] : theme.colors.gray[2],
                                    padding: '2px 4px',
                                    borderRadius: '4px',
                                    fontSize: '90%'
                                  }}>
                                    {children}
                                  </code>
                                ),
                                pre: ({ children }) => (
                                  <pre style={{
                                    margin: '8px 0',
                                    padding: '10px',
                                    backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[8] : theme.colors.gray[2],
                                    borderRadius: '6px',
                                    overflowX: 'auto',
                                    maxWidth: '100%'
                                  }}>
                                    {children}
                                  </pre>
                                ),
                                table: ({ children }) => (
                                  <div style={{
                                    overflowX: 'auto',
                                    margin: '12px 0',
                                    maxWidth: '100%',
                                    borderRadius: '8px',
                                    border: `1px solid ${theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[3]}`
                                  }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '13px' }}>
                                      {children}
                                    </table>
                                  </div>
                                ),
                                thead: ({ children }) => (
                                  <thead style={{ backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[8] : theme.colors.gray[2] }}>
                                    {children}
                                  </thead>
                                ),
                                tbody: ({ children }) => <tbody>{children}</tbody>,
                                tr: ({ children }) => (
                                  <tr style={{
                                    borderBottom: `1px solid ${theme.colorScheme === 'dark' ? theme.colors.dark[5] : theme.colors.gray[3]}`,
                                    backgroundColor: 'transparent'
                                  }}>
                                    {children}
                                  </tr>
                                ),
                                th: ({ children }) => (
                                  <th style={{ padding: '8px 12px', fontWeight: 600, color: theme.colorScheme === 'dark' ? theme.white : theme.black }}>
                                    {children}
                                  </th>
                                ),
                                td: ({ children }) => (
                                  <td style={{ padding: '8px 12px', color: theme.colorScheme === 'dark' ? theme.colors.dark[1] : theme.colors.gray[8] }}>
                                    {children}
                                  </td>
                                ),
                              }}
                            >
                              {msg.text}
                            </ReactMarkdown>
                          </div>
                        )}
                        <Text
                          size="xs"
                          align={isUser ? 'right' : 'left'}
                          style={{
                            fontSize: '10px',
                            marginTop: '4px',
                            color: isUser ? 'rgba(255, 255, 255, 0.7)' : theme.colors.gray[5]
                          }}
                        >
                          {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </Text>
                      </div>
                      {hoveredMessageId === msg.id && (
                        <Tooltip label={copiedMessageId === msg.id ? 'Másolva!' : 'Másolás'} position="top" withArrow>
                          <ActionIcon
                            size="sm"
                            variant="subtle"
                            color="gray"
                            onClick={() => handleCopyText(msg.id, msg.text)}
                            style={{
                              position: 'absolute',
                              right: isUser ? 'auto' : '8px',
                              left: isUser ? '8px' : 'auto',
                              top: '12px',
                              flexShrink: 0
                            }}
                          >
                            {copiedMessageId === msg.id ? <IconCheck size={14} color={theme.colors.green[6]} /> : <IconCopy size={14} />}
                          </ActionIcon>
                        </Tooltip>
                      )}
                    </div>
                  </div>
                )
              })}

              {isTyping && (
                <div style={{ display: 'flex', justifyContent: 'flex-start', width: '100%' }}>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'flex-start',
                      gap: '8px',
                      maxWidth: '85%'
                    }}
                  >
                    <Avatar color="green" radius="xl" size="sm" style={{ marginTop: '4px', flexShrink: 0 }}>
                      <IconSparkles size={16} />
                    </Avatar>
                    <div
                      style={{
                        backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.gray[1],
                        padding: '12px 16px',
                        borderRadius: '16px 16px 16px 2px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: '60px'
                      }}
                    >
                      <Loader size="xs" variant="dots" color="green" />
                    </div>
                  </div>
                </div>
              )}
            </Stack>
          </ScrollArea>

          {/* Chat Input Bar */}
          <div
            style={{
              padding: '16px',
              borderTop: `1px solid ${theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.gray[2]}`,
              backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[8] : theme.white
            }}
          >
            <TextInput
              placeholder="Kérdezz bármit..."
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !isLoading) {
                  handleSendMessage()
                }
              }}
              icon={
                <Tooltip label={isListening ? "Diktálás leállítása" : "Hangalapú kérdezés"} withArrow position="top">
                  <ActionIcon
                    variant="subtle"
                    color={isListening ? "red" : "gray"}
                    radius="xl"
                    size="sm"
                    onClick={toggleListening}
                  >
                    <IconMicrophone size={16} color={isListening ? theme.colors.red[6] : theme.colors.gray[5]} />
                  </ActionIcon>
                </Tooltip>
              }
              rightSection={
                isLoading ? (
                  <Tooltip label="Leállítás" withArrow position="top">
                    <ActionIcon
                      onClick={stopStreaming}
                      variant="filled"
                      color="red"
                      radius="xl"
                      size="md"
                    >
                      <IconPlayerStop size={14} style={{ fill: 'currentColor' }} />
                    </ActionIcon>
                  </Tooltip>
                ) : (
                  <Tooltip label="Küldés" withArrow position="top">
                    <ActionIcon
                      onClick={handleSendMessage}
                      variant="filled"
                      color="green"
                      radius="xl"
                      size="md"
                      disabled={!inputValue.trim()}
                    >
                      <IconSend size={16} style={{ transform: 'translate(-1px, 0px)' }} />
                    </ActionIcon>
                  </Tooltip>
                )
              }
              rightSectionWidth={42}
              radius="xl"
              size="md"
              styles={{
                input: {
                  paddingRight: '48px',
                  borderColor: theme.colorScheme === 'dark' ? theme.colors.dark[5] : theme.colors.gray[3]
                },
                icon: {
                  pointerEvents: 'all'
                }
              }}
            />
          </div>
        </div>
      </Drawer>

      {hasConversation && !aiSidebarOpened && (
        <Tooltip label="beszélgetés folytatása" position="left" withArrow>
          <ActionIcon
            onClick={() => setAiSidebarOpened(true)}
            size={44}
            radius="xl"
            variant="filled"
            color="green"
            style={{
              position: 'fixed',
              bottom: '24px',
              right: '24px',
              zIndex: 99,
              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.25), 0 2px 4px rgba(0, 0, 0, 0.15)',
              transition: 'transform 0.2s ease, background-color 0.2s ease',
            }}
            sx={(theme) => ({
              backgroundColor: theme.colors.green[6],
              '&:hover': {
                backgroundColor: theme.colors.green[7],
                transform: 'scale(1.08)',
              },
              '&:active': {
                transform: 'scale(0.95)',
              }
            })}
          >
            <IconSparkles size={22} />
          </ActionIcon>
        </Tooltip>
      )}
    </>
  )
}
